import { useState, useEffect, useCallback } from 'react';
import dataService from '../services/dataService';

export function useData(fetchFn) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await fetchFn();
        if (mounted) {
          setData(result);
        }
      } catch (err) {
        if (mounted) {
          setError(err.message);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [fetchFn]);

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchFn();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, refresh };
}

// Predefined hooks for common data needs
export function useAccounts() {
  const fetchFn = useCallback(() => dataService.getAccounts(), []);
  return useData(fetchFn);
}

export function useLiabilities() {
  const fetchFn = useCallback(() => dataService.getLiabilities(), []);
  return useData(fetchFn);
}

export function useNetWorth() {
  const fetchFn = useCallback(() => dataService.getNetWorth(), []);
  return useData(fetchFn);
}

export function useNetWorthHistory() {
  const fetchFn = useCallback(() => dataService.getNetWorthHistory(), []);
  return useData(fetchFn);
}

export function useSpendingBreakdown() {
  const fetchFn = useCallback(() => dataService.getSpendingBreakdown(), []);
  return useData(fetchFn);
}

export function useProfile() {
  const fetchFn = useCallback(() => dataService.getProfile(), []);
  return useData(fetchFn);
}

export function useProtection() {
  const fetchFn = useCallback(() => dataService.getProtection(), []);
  return useData(fetchFn);
}

export function useFactFinder() {
  const fetchFn = useCallback(() => dataService.getFactFinder(), []);
  return useData(fetchFn);
}

export function usePaystubs() {
  const fetchFn = useCallback(() => dataService.getPaystubs(), []);
  return useData(fetchFn);
}

export function useTransactions() {
  const fetchFn = useCallback(() => dataService.getTransactions(), []);
  return useData(fetchFn);
}

// New hooks for additional data
export function useNotifications() {
  const fetchFn = useCallback(() => dataService.getNotifications(), []);
  return useData(fetchFn);
}

export function useInsights() {
  const fetchFn = useCallback(() => dataService.getInsights(), []);
  return useData(fetchFn);
}

export function useZecherScore() {
  const fetchFn = useCallback(() => dataService.getZecherScore(), []);
  return useData(fetchFn);
}

export function useAIChatHistory() {
  const fetchFn = useCallback(() => dataService.getAIChatHistory(), []);
  return useData(fetchFn);
} 